import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users as UsersIcon, 
  FileText, 
  BarChart3, 
  Settings, 
  TrendingUp, 
  DollarSign,
  Eye,
  Crown,
  ArrowRight,
  Shield,
  Activity,
  ShieldAlert,
  UserCheck,
} from 'lucide-react';
import { AdminLayout } from '../../components/Admin/AdminLayout';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: '0',
    totalPages: '0',
    totalViews: '0',
    totalUniqueVisitors: '0',
    revenue: '฿0',
    totalBotsBlocked: '0',
    botsBlockedToday: '0',
    userGrowth: '+0%',
    pageGrowth: '+0%',
    viewsGrowth: '+0%',
    revenueGrowth: '+0%',
  });
  const [revenueData, setRevenueData] = useState([]);
  const [userDistribution, setUserDistribution] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  const quickActions = [
    {
      title: 'User Management',
      description: 'View and manage all users',
      icon: <UsersIcon className="w-6 h-6" />,
      path: '/admin/users',
      color: 'blue',
    },
    {
      title: 'Landing Pages',
      description: 'Manage all landing pages',
      icon: <FileText className="w-6 h-6" />,
      path: '/admin/pages',
      color: 'purple',
    },
    {
      title: 'Analytics',
      description: 'View detailed analytics',
      icon: <BarChart3 className="w-6 h-6" />,
      path: '/admin/analytics',
      color: 'green',
    },
    {
      title: 'Financial Reports',
      description: 'View transactions & revenue',
      icon: <DollarSign className="w-6 h-6" />,
      path: '/admin/financial',
      color: 'yellow',
    },
    {
      title: 'System Settings',
      description: 'Configure system settings',
      icon: <Settings className="w-6 h-6" />,
      path: '/admin/settings',
      color: 'orange',
    },
  ];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      if (!token) {
        toast.error('Please login first');
        navigate('/login');
        return;
      }
      
      // Fetch all dashboard data in parallel
      const [statsRes, revenueRes, distRes, activityRes] = await Promise.all([
        fetch(`${API_URL}/admin/dashboard/stats`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_URL}/admin/dashboard/revenue-chart`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_URL}/admin/dashboard/user-distribution`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_URL}/admin/dashboard/recent-activity`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (!statsRes.ok || !revenueRes.ok || !distRes.ok || !activityRes.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const statsData = await statsRes.json();
      const revenueChart = await revenueRes.json();
      const distribution = await distRes.json();
      const activity = await activityRes.json();

      // Format stats
      setStats({
        totalUsers: statsData.totalUsers.toLocaleString(),
        totalPages: statsData.totalPages.toLocaleString(),
        totalViews: formatNumber(statsData.totalViews),
        totalUniqueVisitors: formatNumber(statsData.totalUniqueVisitors || 0),
        revenue: `฿${statsData.totalRevenue.toLocaleString()}`,
        totalBotsBlocked: formatNumber(statsData.totalBotsBlocked || 0),
        botsBlockedToday: (statsData.botsBlockedToday || 0).toLocaleString(),
        userGrowth: statsData.userGrowth,
        pageGrowth: statsData.pageGrowth,
        viewsGrowth: statsData.viewsGrowth,
        revenueGrowth: statsData.revenueGrowth,
      });

      setRevenueData(revenueChart);
      setUserDistribution(distribution);
      setRecentActivity(activity);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'user_signup':
        return <UsersIcon className="w-4 h-4 text-blue-600" />;
      case 'page_created':
        return <FileText className="w-4 h-4 text-purple-600" />;
      case 'upgrade':
        return <Crown className="w-4 h-4 text-yellow-600" />;
      case 'page_published':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      default:
        return <Shield className="w-4 h-4 text-gray-600" />;
    }
  };

  const getActivityText = (activity: any) => {
    switch (activity.type) {
      case 'user_signup':
        return `${activity.user} signed up`;
      case 'page_created':
        return `${activity.user} created "${activity.page}"`;
      case 'upgrade':
        return `${activity.user} upgraded to Pro`;
      case 'page_published':
        return `${activity.user} published "${activity.page}"`;
      default:
        return activity.user;
    }
  };

  const getColorClasses = (color: string) => {
    const colors: any = {
      blue: {
        bg: 'bg-blue-50',
        text: 'text-blue-600',
        hover: 'hover:bg-blue-100',
        border: 'border-blue-200',
      },
      purple: {
        bg: 'bg-purple-50',
        text: 'text-purple-600',
        hover: 'hover:bg-purple-100',
        border: 'border-purple-200',
      },
      green: {
        bg: 'bg-green-50',
        text: 'text-green-600',
        hover: 'hover:bg-green-100',
        border: 'border-green-200',
      },
      yellow: {
        bg: 'bg-yellow-50',
        text: 'text-yellow-600',
        hover: 'hover:bg-yellow-100',
        border: 'border-yellow-200',
      },
      orange: {
        bg: 'bg-orange-50',
        text: 'text-orange-600',
        hover: 'hover:bg-orange-100',
        border: 'border-orange-200',
      },
    };
    return colors[color] || colors.blue;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Activity className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const displayStats = [
    { 
      title: 'Total Users', 
      value: stats.totalUsers, 
      change: stats.userGrowth,
      icon: <UsersIcon className="w-6 h-6" />,
      color: 'blue',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      changeColor: 'text-green-600'
    },
    { 
      title: 'Landing Pages', 
      value: stats.totalPages, 
      change: stats.pageGrowth,
      icon: <FileText className="w-6 h-6" />,
      color: 'purple',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      changeColor: 'text-green-600'
    },
    { 
      title: 'Total Views', 
      value: stats.totalViews, 
      change: stats.viewsGrowth,
      icon: <Eye className="w-6 h-6" />,
      color: 'green',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      changeColor: 'text-green-600'
    },
    { 
      title: 'Unique Visitors', 
      value: stats.totalUniqueVisitors, 
      change: 'all time',
      icon: <UserCheck className="w-6 h-6" />,
      color: 'indigo',
      bgColor: 'bg-indigo-50',
      textColor: 'text-indigo-600',
      changeColor: 'text-indigo-600'
    },
    { 
      title: 'Revenue', 
      value: stats.revenue, 
      change: stats.revenueGrowth,
      icon: <DollarSign className="w-6 h-6" />,
      color: 'orange',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      changeColor: 'text-green-600'
    },
    { 
      title: 'Bots Blocked', 
      value: stats.totalBotsBlocked, 
      change: `${stats.botsBlockedToday} today`,
      icon: <ShieldAlert className="w-6 h-6" />,
      color: 'red',
      bgColor: 'bg-red-50',
      textColor: 'text-red-600',
      changeColor: 'text-red-600'
    },
  ];

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back! Here's what's happening.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {displayStats.map((stat, index) => (
            <div key={index} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className={`${stat.bgColor} ${stat.textColor} p-3 rounded-lg`}>
                  {stat.icon}
                </div>
                <span className={`text-sm font-semibold ${stat.changeColor}`}>
                  {stat.change}
                </span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{stat.value}</h3>
              <p className="text-sm text-gray-600 mt-1">{stat.title}</p>
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Revenue Chart */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Revenue Overview</h3>
                <p className="text-sm text-gray-600">Monthly revenue trend</p>
              </div>
              <button 
                onClick={() => navigate('/admin/financial')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                View Details
              </button>
            </div>
            {revenueData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip />
                  <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fillOpacity={1} fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400">
                <p>No revenue data available</p>
              </div>
            )}
          </div>

          {/* User Distribution */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-900">User Distribution</h3>
              <p className="text-sm text-gray-600">Free vs Pro users</p>
            </div>
            {userDistribution.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={userDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {userDistribution.map((entry: any, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {userDistribution.map((item: any, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                        <span className="text-sm text-gray-600">{item.name}</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{item.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400">
                <p>No user data available</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions & Recent Activity */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <div className="lg:col-span-2">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {quickActions.map((action, index) => {
                const colors = getColorClasses(action.color);
                return (
                  <button
                    key={index}
                    onClick={() => navigate(action.path)}
                    className={`bg-white border border-gray-200 rounded-xl p-5 text-left ${colors.hover} transition-all hover:shadow-md group`}
                  >
                    <div className="flex items-start justify-between">
                      <div className={`${colors.bg} ${colors.text} p-3 rounded-lg mb-3`}>
                        {action.icon}
                      </div>
                      <ArrowRight className={`w-5 h-5 ${colors.text} opacity-0 group-hover:opacity-100 transition-opacity`} />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">{action.title}</h4>
                    <p className="text-sm text-gray-600">{action.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Recent Activity */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Activity</h3>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              {recentActivity.length > 0 ? (
                <div className="space-y-4">
                  {recentActivity.map((activity: any, index) => (
                    <div key={index} className="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                      <div className="bg-gray-50 p-2 rounded-lg">
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{getActivityText(activity)}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{activity.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No recent activity</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};
